import React, { useContext, memo, useMemo } from 'react'
import {
  TableHead as MuiTableHead,
  TableRow,
  Checkbox,
  TableCell,
  TableSortLabel,
  lighten,
} from '@mui/material'
import { makeStyles } from '@mui/styles'
import { isArray } from 'lodash'
import clsx from 'clsx'
import { TableContext } from '../Table/Table'

const useStyles = makeStyles((theme) => ({
  highlight:
    theme.palette.mode === 'light'
      ? {
          color: theme.palette.secondary.main,
          backgroundColor: lighten(theme.palette.secondary.light, 0.85),
        }
      : {
          color: theme.palette.text.primary,
          backgroundColor: theme.palette.secondary.dark,
        },
  lighthighlight:
    theme.palette.mode === 'light'
      ? {
          background: 'rgba(41, 121, 255, 0.08)',
        }
      : {
          background: 'rgba(41, 121, 255, 0.16)',
        },
  visuallyHidden: {
    border: 0,
    clip: 'rect(0 0 0 0)',
    height: 1,
    margin: -1,
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    top: 20,
    width: 1,
  },
}))

// eslint-disable-next-line react/display-name
const TableHead = memo(
  ({
    numSelected,
    rowCount,
    head,
    enableCheck,
    disableCheckIf,
    line,
    size,
    headerColumnStyles,
    disableSorting,
    ordCols,
    cortaDados,
  }) => {
    const classes = useStyles()
    const { selected, order, orderBy, dispatch, lista, indexToSelect, method } =
      useContext(TableContext)

    const handleRequestSort = (property) => {
      const isAsc = orderBy === property && order === 'asc'
      dispatch({ type: 'setOrder', value: isAsc ? 'desc' : 'asc' })
      dispatch({ type: 'setOrderBy', value: property })
    }

    const length = useMemo(
      () =>
        disableCheckIf
          ? lista.reduce(
              (acc, linha, i) =>
                !disableCheckIf(linha, line(linha), i) ? acc + 1 : acc,
              0,
            )
          : size,
      [disableCheckIf, line, lista, size],
    )

    const handleSelectAllClick = (event) => {
      if (event.target.checked) {
        if (!method && selected.length === 0)
          dispatch({
            type: 'setSelected',
            value: cortaDados(lista)
              .filter((linha, i) =>
                disableCheckIf ? !disableCheckIf(linha, line(linha), i) : true,
              )
              .map((n) => n[indexToSelect]),
          })
        else if (selected.length < length)
          dispatch({
            type: 'setSelected',
            value: lista
              .filter((linha, i) =>
                disableCheckIf ? !disableCheckIf(linha, line(linha), i) : true,
              )
              .map((n) => n[indexToSelect]),
          })
        else dispatch({ type: 'setSelected', value: [] })
      } else dispatch({ type: 'setSelected', value: [] })
    }

    return (
      <MuiTableHead>
        <TableRow
          className={clsx({
            [classes.highlight]:
              (numSelected === rowCount || numSelected === length) &&
              rowCount &&
              length,
            [classes.lighthighlight]: numSelected > 0 && numSelected < rowCount,
          })}
        >
          {enableCheck && (
            <TableCell padding="checkbox">
              <Checkbox
                disabled={!length}
                indeterminate={
                  method
                    ? numSelected > 0 && numSelected < rowCount
                    : numSelected > 0 && numSelected < length
                }
                checked={
                  method
                    ? rowCount > 0 && numSelected === rowCount
                    : rowCount > 0 && length && numSelected === length
                }
                onChange={handleSelectAllClick}
              />
            </TableCell>
          )}
          {isArray(head) &&
            head.map((item, i) => (
              <TableCell
                key={i}
                style={headerColumnStyles ? headerColumnStyles[i] : undefined}
              >
                {disableSorting[i] || (ordCols && !ordCols[i]) ? (
                  item
                ) : (
                  <TableSortLabel
                    active={orderBy === i}
                    direction={orderBy === i ? order : 'asc'}
                    onClick={() => handleRequestSort(i)}
                  >
                    {item}
                    {orderBy === i ? (
                      <span className={classes.visuallyHidden}>
                        {order === 'desc'
                          ? 'sorted descending'
                          : 'sorted ascending'}
                      </span>
                    ) : null}
                  </TableSortLabel>
                )}
              </TableCell>
            ))}
        </TableRow>
      </MuiTableHead>
    )
  },
)

export default TableHead
